import api from "@/api";
import { defineStore } from "pinia";

/**
 * Moves of schools between regional clusters (dashboard-api
 * /v2/access/region-moves). Deliberately not persisted: a move's state lives
 * on the server and changes underneath the page, so it is always fetched.
 */

export type MoveStatus =
  | "draft"
  | "preflighting"
  | "preflighted"
  | "approved"
  | "copying"
  | "verifying"
  | "cutting_over"
  | "holding"
  | "sealing"
  | "reconciling"
  | "quarantining"
  | "cleaning"
  | "completed"
  | "failed"
  | "aborting"
  | "aborted"
  | "rolling_back"
  | "rolled_back";

export interface Finding {
  code: string;
  message: string;
  ids?: Array<string | { id: string; index?: string }>;
}

export interface ScopeAdmin {
  id: string;
  email?: string;
  name?: string;
  kind: "move" | "stay" | "spanning" | "internal";
  lands: "source" | "destination";
  decided?: boolean;
}

export interface MoveSummary {
  _id: string;
  /** "learners" moves learners into a school the destination already has. */
  kind?: "schools" | "learners" | "transfer";
  from: string;
  to: string;
  label: string;
  status: MoveStatus;
  phase: string | null;
  failedPhase: string | null;
  error: { code: string; message: string } | null;
  rollbackOf: string | null;
  rollbackMoveId: string | null;
  blockers: number | null;
  warnings: number | null;
  conflicts: number;
  createdBy: { uid: string; email: string } | null;
  approvedBy: { uid: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface MoveDetail extends Omit<MoveSummary, "conflicts"> {
  /** What the operator asked for; a learner move names its schools here. */
  request?: { schoolIds?: string[]; userIds?: string[] };
  confirmName: string;
  /** Shows a running status, but its worker died and its lease ran out. */
  stalled?: boolean;
  adminDecisions: Record<string, "source" | "destination">;
  reuseAdminIds?: string[];
  cutoverStartedAt?: string;
  holdUntil?: string;
  report?: {
    at: string;
    blockers: Finding[];
    warnings: Finding[];
    counts: Record<string, number>;
    scope: {
      counts: Record<string, number>;
      groups: Array<{ id: string; name: string; kind: "move" | "split"; schoolsInGroup: number; schoolsMoving: number }>;
      roles: Array<{ id: string; name: string; kind: string }>;
      admins: ScopeAdmin[];
    };
    adminActions?: Array<{
      id: string;
      action: "move" | "stay" | "review" | "reuse";
      canReuse: boolean;
      reason?: string;
      addedRoles: Array<{ id: string; name: string; classes: string[] }>;
    }>;
    learnerPlan?: {
      candidates: number;
      alreadyThere: number;
      moving: number;
      classesCopied: number;
      classesThere: number;
      listsCopied?: number;
      classesKept?: number;
      listsKept?: number;
      changingSchool?: boolean;
    };
    transfer?: { learners: number; school: { _id: string; name: string; status: string } | null; classes: number };
  };
  progress?: Record<string, Record<string, { docs?: number; batches?: number; done?: boolean; count?: number }>>;
  delta?: { conflicts: number; deletedAtSource: Record<string, number> };
  conflicts?: { count: number; sample: Array<{ collection: string; docId: unknown; reason: string }> } | number;
  history: Array<{ at: string; status?: string; action?: string; phase?: string; error?: string; note?: string; by?: { email?: string } }>;
}

export interface MismatchedSchool {
  _id: string;
  name: string;
  country: string;
  status: string;
  group: { _id: string; name: string } | null;
  createdAt: string;
  belongs: string;
}

/** A school here marked as a read-only copy of one that lives in another region. */
export interface MovedSchool {
  _id: string;
  name: string;
  country: string;
  group: { _id: string; name: string } | null;
  movedTo: string;
  movedAt: string;
  movedBy: { uid: string; email: string } | null;
}

/**
 * Whether the other region holds a school here (a leftover copy, which can be
 * marked as moved), and how many of its learners here it lacks.
 */
export interface LeftoverStatus {
  _id: string;
  there: boolean;
  theirStatus: string | null;
  learners: number;
  learnersOnlyHere: number;
}

/** A school to change learners into, with its classes. */
export interface SchoolOption {
  _id: string;
  name: string;
  classes: Array<{ _id: string; name: string; gradeLevel: number | null }>;
}

export interface SchoolLearner {
  _id: string;
  name: string;
  email: string | null;
  classes: string[];
}

const BASE = "v2/access/region-moves";

function errorText(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === "string" ? data.error : fallback;
}

export const useRegionMoveStore = defineStore({
  id: "regionMoveStore",
  state: () => ({
    region: "" as string,
    moves: [] as MoveSummary[],
    mismatches: [] as MismatchedSchool[],
    movedSchools: [] as MovedSchool[],
    leftovers: {} as Record<string, LeftoverStatus>,
    leftoversChecking: false,
    leftoversError: "" as string,
    current: null as MoveDetail | null,
    loading: false,
    acting: false,
    error: "" as string
  }),
  actions: {
    async fetchMoves() {
      const { data } = await api.get(BASE);
      this.region = data.data.region;
      this.moves = data.data.moves;
    },
    async fetchMismatches() {
      const { data } = await api.get(`${BASE}/mismatches`);
      this.region = data.data.region;
      this.mismatches = data.data.schools;
    },
    async fetchMovedSchools() {
      const { data } = await api.get(`${BASE}/moved-schools`);
      this.movedSchools = data.data.schools;
    },
    /** Ask the other region about every school on the page; slow, so not part of the poll. */
    async fetchLeftovers() {
      const byRegion = new Map<string, string[]>();
      const add = (to: string, id: string) => byRegion.set(to, [...(byRegion.get(to) || []), id]);
      this.mismatches.forEach(s => add(s.belongs, s._id));
      this.movedSchools.forEach(s => add(s.movedTo, s._id));
      const batches: Array<[string, string[]]> = [];
      byRegion.forEach((ids, to) => {
        for (let i = 0; i < ids.length; i += 500) batches.push([to, ids.slice(i, i + 500)]);
      });
      this.leftoversChecking = true;
      this.leftoversError = "";
      try {
        const results = await Promise.all(batches.map(([to, schoolIds]) => api.post(`${BASE}/schools/leftover-status`, { schoolIds, to })));
        const next: Record<string, LeftoverStatus> = {};
        results.forEach(({ data }) => (data.data.schools as LeftoverStatus[]).forEach(s => (next[s._id] = s)));
        this.leftovers = next;
      } catch (error) {
        this.leftoversError = errorText(error, "Could not ask the other region which schools it holds.");
      } finally {
        this.leftoversChecking = false;
      }
    },
    async markMoved(schoolIds: string[], to: string) {
      return this.act(() => api.post(`${BASE}/schools/mark-moved`, { schoolIds, to }), "The schools could not be marked as moved.");
    },
    async unmarkMoved(schoolIds: string[]) {
      return this.act(() => api.post(`${BASE}/schools/unmark-moved`, { schoolIds }), "The schools could not be unmarked.");
    },
    async fetchMove(id: string) {
      const { data } = await api.get(`${BASE}/${id}`);
      this.current = data.data;
      return this.current;
    },
    /** Run a mutating call, keep the error the server gave, then refresh. */
    async act<T>(fn: () => Promise<T>, fallback: string): Promise<T | null> {
      this.acting = true;
      this.error = "";
      try {
        return await fn();
      } catch (error) {
        this.error = errorText(error, fallback);
        return null;
      } finally {
        this.acting = false;
      }
    },
    async create(schoolIds: string[], to: string) {
      return this.act(async () => {
        const { data } = await api.post(BASE, { schoolIds, to });
        await this.fetchMoves();
        return data.data as MoveSummary;
      }, "The move could not be created.");
    },
    async createLearnerMove(schoolIds: string[], userIds: string[], to: string) {
      return this.act(async () => {
        const { data } = await api.post(`${BASE}/learners`, { schoolIds, userIds, to });
        await this.fetchMoves();
        return data.data as MoveSummary;
      }, "The learner move could not be created.");
    },
    async searchSchools(region: string, q: string): Promise<SchoolOption[]> {
      const { data } = await api.get(`${BASE}/schools`, { params: { region, q } });
      return data.data.schools;
    },
    async fetchSchoolLearners(schoolId: string): Promise<SchoolLearner[]> {
      const { data } = await api.get(`${BASE}/schools/${schoolId}/learners`);
      return data.data;
    },
    /** Learners change school: here directly, or into another region's school as a learner move. */
    async changeSchool(userIds: string[], to: string, school: SchoolOption, classIds: string[]) {
      return this.act(async () => {
        const { data } =
          to === this.region
            ? await api.post(`${BASE}/transfers`, { userIds, schoolId: school._id, classIds })
            : await api.post(`${BASE}/learners`, { userIds, to, targetSchoolId: school._id, targetClassIds: classIds, targetSchoolName: school.name });
        await this.fetchMoves();
        return data.data as MoveSummary;
      }, "The school change could not be started.");
    },
    async saveDecisions(id: string, adminDecisions: Record<string, string>, reuseAdminIds?: string[]) {
      return this.act(() => api.post(`${BASE}/${id}/decisions`, { adminDecisions, ...(reuseAdminIds ? { reuseAdminIds } : {}) }), "The assignments could not be saved.");
    },
    async preflight(id: string) {
      return this.act(() => api.post(`${BASE}/${id}/preflight`), "Preflight could not be started.");
    },
    async approve(id: string, confirmName: string) {
      return this.act(() => api.post(`${BASE}/${id}/approve`, { confirmName }), "The move could not be approved.");
    },
    async resume(id: string) {
      return this.act(() => api.post(`${BASE}/${id}/resume`), "The move could not be resumed.");
    },
    async abort(id: string) {
      return this.act(() => api.post(`${BASE}/${id}/abort`), "The move could not be aborted.");
    },
    async rollback(id: string, confirmName: string) {
      return this.act(() => api.post(`${BASE}/${id}/rollback`, { confirmName }), "The rollback could not be started.");
    }
  }
});
